import {
  COLUMN_ADDITION_MULTI_PAGE,
  COLUMN_MOVEMENT_MULTI_PAGE,
} from "../config/gridLinesConfig";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Chip,
  TextField,
} from "@mui/material";
import React, { useState, useEffect } from "react";

const GridLineConfirmationModal = ({
  open,
  onClose,
  pendingGridLineChanges,
  currentPage,
  isMultiPageTable,
  getTablePages,
  getCellDifferences,
  renderTablePreview,
  beforeTableRef,
  afterTableRef,
  handleTableScroll,
  onConfirm,
  onCancel,
  selectedTable,
  pdfPageDimensions,
  handleCellDetailClick,
  modalState,
}) => {
  // State for column name input (must be called before early return)
  const [newColumnName, setNewColumnName] = useState("");

  // Detect change types based on actual grid line analysis (safer than relying on changeType)
  const detectChangeType = () => {
    if (!pendingGridLineChanges)
      return { isNewColumn: false, isNewRow: false, isMove: false };

    // Check table structure changes first
    const originalCols =
      pendingGridLineChanges.originalTableData?.headers?.length || 0;
    const newCols = pendingGridLineChanges.newTableData?.headers?.length || 0;
    const originalRows =
      pendingGridLineChanges.originalTableData?.rows?.length || 0;
    const newRows = pendingGridLineChanges.newTableData?.rows?.length || 0;

    // Check grid line counts across ALL pages for multi-page operations
    let isNewColumn = false;
    let isNewRow = false;

    // For column additions, check if ANY page has more vertical lines
    if (
      pendingGridLineChanges.originalGridLines &&
      pendingGridLineChanges.newGridLines
    ) {
      const allPages = new Set([
        ...Object.keys(pendingGridLineChanges.originalGridLines),
        ...Object.keys(pendingGridLineChanges.newGridLines),
      ]);

      let hasVerticalIncrease = false;
      let hasHorizontalIncrease = false;

      allPages.forEach((pageNum) => {
        const originalPageLines =
          pendingGridLineChanges.originalGridLines[pageNum];
        const newPageLines = pendingGridLineChanges.newGridLines[pageNum];

        if (originalPageLines && newPageLines) {
          const originalVerticalCount = originalPageLines.vertical?.length || 0;
          const newVerticalCount = newPageLines.vertical?.length || 0;
          const originalHorizontalCount =
            originalPageLines.horizontal?.length || 0;
          const newHorizontalCount = newPageLines.horizontal?.length || 0;

          if (newVerticalCount > originalVerticalCount) {
            hasVerticalIncrease = true;
          }
          if (newHorizontalCount > originalHorizontalCount) {
            hasHorizontalIncrease = true;
          }
        }
      });

      // New column: more vertical lines on ANY page AND more table columns
      isNewColumn = hasVerticalIncrease && newCols > originalCols;

      // New row: more horizontal lines on ANY page AND more table rows
      isNewRow = hasHorizontalIncrease && newRows > originalRows;
    }

    const isMove = !isNewColumn && !isNewRow; // Regular gridline movement

    return { isNewColumn, isNewRow, isMove };
  };

  const { isNewColumn, isNewRow } = detectChangeType();

  // Set default column name when modal opens for new columns
  useEffect(() => {
    if (open && isNewColumn && pendingGridLineChanges) {
      const newColumnCount =
        pendingGridLineChanges.newTableData?.headers?.length || 1;
      setNewColumnName(`Column ${newColumnCount}`);
    }
  }, [open, isNewColumn, pendingGridLineChanges]);

  // Early return after hooks
  if (!pendingGridLineChanges) return null;

  // Handle confirm with column name for new columns
  const handleConfirm = () => {
    if (isNewColumn) {
      onConfirm(newColumnName.trim());
    } else {
      onConfirm();
    }
  };

  // Handle Enter key press for column name input
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && newColumnName.trim()) {
      handleConfirm();
    }
  };

  // Helper function to determine layout direction
  const determineLayoutDirection = () => {
    // Use our new detection logic instead of changeType
    if (isNewRow) return false; // Row changes use vertical layout
    if (isNewColumn) return true; // Column changes use side-by-side layout

    // Compare original vs new grid lines across ALL pages to see what actually moved
    if (
      pendingGridLineChanges.originalGridLines &&
      pendingGridLineChanges.newGridLines
    ) {
      const allPages = new Set([
        ...Object.keys(pendingGridLineChanges.originalGridLines),
        ...Object.keys(pendingGridLineChanges.newGridLines),
      ]);

      let xValuesChanged = false;
      let yValuesChanged = false;

      // Check changes across all pages
      allPages.forEach((pageNum) => {
        const originalGridLines =
          pendingGridLineChanges.originalGridLines[pageNum];
        const newGridLines = pendingGridLineChanges.newGridLines[pageNum];

        if (!originalGridLines || !newGridLines) return;

        // Check vertical grid lines (column boundaries) - compare X positions
        const originalVertical = originalGridLines.vertical || [];
        const newVertical = newGridLines.vertical || [];

        if (originalVertical.length === newVertical.length) {
          for (let i = 0; i < originalVertical.length; i++) {
            const originalX = originalVertical[i]?.x0;
            const newX = newVertical[i]?.x0;
            if (
              originalX !== undefined &&
              newX !== undefined &&
              Math.abs(originalX - newX) > 2
            ) {
              xValuesChanged = true;
              break;
            }
          }
        } else {
          xValuesChanged = true; // Different number of vertical lines
        }

        // Check horizontal grid lines (row boundaries) - compare Y positions
        const originalHorizontal = originalGridLines.horizontal || [];
        const newHorizontal = newGridLines.horizontal || [];

        if (originalHorizontal.length === newHorizontal.length) {
          for (let i = 0; i < originalHorizontal.length; i++) {
            const originalY = originalHorizontal[i]?.y0;
            const newY = newHorizontal[i]?.y0;
            if (
              originalY !== undefined &&
              newY !== undefined &&
              Math.abs(originalY - newY) > 2
            ) {
              yValuesChanged = true;
              break;
            }
          }
        } else {
          yValuesChanged = true; // Different number of horizontal lines
        }
      });

      // Apply layout rule based on what changed:
      if (xValuesChanged && !yValuesChanged) {
        return true; // Column change - use side-by-side layout
      }

      if (yValuesChanged && !xValuesChanged) {
        return false; // Row change - use vertical layout
      }

      if (xValuesChanged && yValuesChanged) {
        return true; // Default to column change for mixed
      }
    }

    // Check column count changes
    const oldCols = pendingGridLineChanges.originalTableData.headers.length;
    const newCols = pendingGridLineChanges.newTableData.headers.length;
    const oldRows = pendingGridLineChanges.originalTableData.rows.length;
    const newRows = pendingGridLineChanges.newTableData.rows.length;

    // If columns changed but not rows, it's a column change
    if (oldCols !== newCols && oldRows === newRows) {
      return true;
    }

    // If rows changed but not columns, it's a row change
    if (oldRows !== newRows && oldCols === newCols) {
      return false;
    }

    // Default to vertical layout if no clear detection
    return false;
  };

  const isColumnChange = determineLayoutDirection();

  const getChangeDescription = () => {
    const originalColumnCount =
      pendingGridLineChanges?.originalTableData?.headers?.length || 0;
    const newColumnCount =
      pendingGridLineChanges?.newTableData?.headers?.length || 0;
    const originalRowCount =
      pendingGridLineChanges?.originalTableData?.rows?.length || 0;
    const newRowCount = pendingGridLineChanges?.newTableData?.rows?.length || 0;

    // Use our new detection logic
    const isStructuralChange = isNewColumn || isNewRow;

    if (isStructuralChange) {
      let changeDescription = "You have added ";
      const changes = [];

      if (isNewColumn) {
        const addedCount = newColumnCount - originalColumnCount;
        changes.push(`${addedCount} new column${addedCount > 1 ? "s" : ""}`);
      }

      if (isNewRow) {
        const addedCount = newRowCount - originalRowCount;
        changes.push(`${addedCount} new row${addedCount > 1 ? "s" : ""}`);
      }

      changeDescription += changes.join(" and ");
      changeDescription +=
        " to the table. This will split existing content and redistribute words between cells based on their position relative to the new grid boundaries.";

      return changeDescription;
    } else {
      return "You have moved grid lines which will reorganize the table content. This will redistribute words between cells based on their position relative to the new grid boundaries.";
    }
  };

  const renderChangesDetected = () => {
    const changes = [];
    const oldCols = pendingGridLineChanges.originalTableData.headers.length;
    const newCols = pendingGridLineChanges.newTableData.headers.length;
    const oldRows = pendingGridLineChanges.originalTableData.rows.length;
    const newRows = pendingGridLineChanges.newTableData.rows.length;

    // Always calculate cell differences for all types of changes
    let changedCellCount = 0;
    let differences = null;

    if (isNewColumn && pendingGridLineChanges.splitColumnIndex !== undefined) {
      // For new columns, only count rows that had content in the split column
      const splitColIndex = pendingGridLineChanges.splitColumnIndex;
      const originalData = pendingGridLineChanges.originalTableData;

      changedCellCount = originalData.rows.filter((row) => {
        return row[splitColIndex] && row[splitColIndex].trim() !== "";
      }).length;
    } else {
      // For all other changes (movements and other additions), use the same calculation as tablePreviewUtils.js
      try {
        // Use the exact same getCellDifferences call as in tablePreviewUtils.js
        differences = pendingGridLineChanges
          ? getCellDifferences(
              pendingGridLineChanges.originalTableData,
              pendingGridLineChanges.newTableData,
              pendingGridLineChanges,
              selectedTable,
              pdfPageDimensions
            )
          : { changes: new Set(), cellMap: {}, changedColumns: new Set() };

        changedCellCount = differences?.changes?.size || 0;
      } catch (error) {
        changedCellCount = 0;
        differences = {
          changes: new Set(),
          cellMap: {},
          changedColumns: new Set(),
        };
      }
    }

    // Show structural changes (additions/removals)
    if (oldCols !== newCols) {
      changes.push(
        <Chip
          key={"cols"}
          label={`Columns: ${oldCols} → ${newCols}`}
          color={newCols > oldCols ? "success" : "warning"}
          size={"small"}
          sx={{ mr: 1, mb: 1 }}
        />
      );
    }

    if (oldRows !== newRows) {
      changes.push(
        <Chip
          key={"rows"}
          label={`Rows: ${oldRows} → ${newRows}`}
          color={newRows > oldRows ? "success" : "warning"}
          size={"small"}
          sx={{ mr: 1, mb: 1 }}
        />
      );
    }

    // For movements (when structure is same but content changed), show movement info
    if (
      oldCols === newCols &&
      oldRows === newRows &&
      !isNewColumn &&
      !isNewRow
    ) {
      // This is a movement operation
      const { isMove } = detectChangeType();

      if (isMove) {
        // Determine what type of movement this is
        const isColumnMovement = determineLayoutDirection();

        changes.push(
          <Chip
            key={"movement"}
            label={
              isColumnMovement
                ? "Column boundaries moved"
                : "Row boundaries moved"
            }
            color={"primary"}
            size={"small"}
            sx={{ mr: 1, mb: 1 }}
          />
        );
      }
    }

    // Use the same cell count calculation as tablePreviewUtils.js
    const finalCellCount = changedCellCount;

    // Show cell modification count if we have any changes
    if (finalCellCount > 0) {
      changes.push(
        <Chip
          key={"redistribution"}
          label={`${finalCellCount} cells modified`}
          color={"info"}
          size={"small"}
          sx={{ mr: 1, mb: 1 }}
        />
      );
    }

    return changes.length > 0 ? (
      changes
    ) : (
      <Chip
        label={"No content changes detected"}
        color={"success"}
        size={"small"}
      />
    );
  };

  const renderLegend = () => (
    <Box
      sx={{
        mb: 2,
        display: "flex",
        gap: 2,
        flexWrap: "wrap",
        fontSize: "0.75rem",
        alignItems: "center",
      }}
    >
      <Typography variant={"body2"} sx={{ fontWeight: "bold", mr: 1 }}>
        {"Legend:"}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box
          sx={{
            width: "14px",
            height: "14px",
            backgroundColor: "#f8d7da",
            border: "2px solid #dc3545",
            borderRadius: "2px",
          }}
        />
        <Typography variant={"caption"} sx={{ fontWeight: "500" }}>
          {"Removed"}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box
          sx={{
            width: "14px",
            height: "14px",
            backgroundColor: "#d4edda",
            border: "2px solid #28a745",
            borderRadius: "2px",
          }}
        />
        <Typography variant={"caption"} sx={{ fontWeight: "500" }}>
          {"Added"}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box
          sx={{
            width: "14px",
            height: "14px",
            backgroundColor: "#cfe2ff",
            border: "2px solid #007bff",
            borderRadius: "2px",
          }}
        />
        <Typography variant={"caption"} sx={{ fontWeight: "500" }}>
          {"Modified"}
        </Typography>
      </Box>
      <Typography
        variant={"caption"}
        sx={{
          fontSize: "0.7rem",
          color: "text.primary",
          fontWeight: "500",
        }}
      >
        {
          "● = Changed cell • Click any cell for full details • Only showing columns with changes"
        }
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          width: "90vw",
          maxWidth: "1200px",
          height: "95vh",
          maxHeight: "1000px",
          margin: "1vh auto",
        },
      }}
    >
      <DialogTitle>{"Confirm Changes"}</DialogTitle>
      <DialogContent
        sx={{
          overflow: "auto",
          maxHeight: "calc(95vh - 120px)",
        }}
      >
        <Typography variant={"body1"} sx={{ mb: 2 }}>
          {getChangeDescription()}
        </Typography>

        <Typography variant={"body2"} color={"warning.main"} sx={{ mb: 2 }}>
          <strong>{"Note:"}</strong>{" "}
          {
            "This action will update the table structure and cannot be easily undone. "
          }
          {"Make sure the changes look correct before applying."}
        </Typography>

        {isMultiPageTable &&
          isMultiPageTable() &&
          ((isNewColumn && COLUMN_ADDITION_MULTI_PAGE) ||
            (isColumnChange && !isNewColumn && COLUMN_MOVEMENT_MULTI_PAGE)) && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: "warning.light",
                borderRadius: 1,
              }}
            >
              <Typography variant={"body2"} color={"warning.contrastText"}>
                <strong>{"Multi-page Table Detected:"}</strong>
                {" This table spans pages "}
                {getTablePages && getTablePages().join(", ")}
                {". "}
                {"Changes will affect the structure across all pages."}
              </Typography>
            </Box>
          )}

        {/* Changes detected section */}
        <Typography
          variant={"body2"}
          color={"text.secondary"}
          sx={{ mb: 1, fontWeight: "bold" }}
        >
          {"Changes detected:"}
        </Typography>

        <Box sx={{ mb: 3 }}>{renderChangesDetected()}</Box>

        {/* Column Name Input (only for new columns) */}
        {isNewColumn && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant={"body2"}
              color={"text.secondary"}
              sx={{ mb: 1, fontWeight: "bold" }}
            >
              {"New Column Name:"}
            </Typography>
            <TextField
              fullWidth
              size={"small"}
              value={newColumnName}
              onChange={(e) => setNewColumnName(e.target.value)}
              placeholder={"Enter column name..."}
              onKeyPress={handleKeyPress}
              sx={{
                backgroundColor: "white",
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#1976d2",
                  },
                },
              }}
            />
          </Box>
        )}

        {/* Legend for color coding */}
        {renderLegend()}

        {/* All changes (including new columns) use the same preview */}
        <>
          <Typography variant={"body2"} color={"text.secondary"} sx={{ mb: 2 }}>
            {(() => {
              const baseText =
                "Table comparison - only columns with changes are shown";
              const layoutInfo = isColumnChange
                ? " (showing side-by-side for column changes)"
                : " (showing vertically for row changes)";
              return baseText + layoutInfo + ":";
            })()}
          </Typography>

          <Paper
            sx={{
              p: 2,
              backgroundColor: "grey.50",
              border: "1px solid",
              borderColor: "grey.300",
              mb: 2,
              height: "600px",
              overflow: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: isColumnChange ? "row" : "column",
                gap: 3,
                height: "100%",
                overflow: "auto",
              }}
            >
              {/* Before - left or top depending on layout */}
              <Box
                sx={{
                  flex: isColumnChange ? 1 : "none",
                  minWidth: isColumnChange ? 0 : "auto",
                }}
              >
                {renderTablePreview &&
                  (() => {
                    // For column splits, only show the split column and the new column
                    if (
                      isNewColumn &&
                      pendingGridLineChanges.splitColumnIndex !== undefined
                    ) {
                      return renderTablePreview(
                        pendingGridLineChanges.originalTableData,
                        "Before: (1 columns with changes)",
                        false,
                        beforeTableRef,
                        handleTableScroll && handleTableScroll(true),
                        pendingGridLineChanges,
                        selectedTable,
                        pdfPageDimensions,
                        currentPage,
                        handleCellDetailClick,
                        modalState
                      );
                    } else if (
                      isNewRow &&
                      pendingGridLineChanges.splitRowIndex !== undefined
                    ) {
                      // For row splits, only show the original row that will be split

                      return renderTablePreview(
                        pendingGridLineChanges.originalTableData,
                        "Before: (1 row with changes)",
                        false,
                        beforeTableRef,
                        handleTableScroll && handleTableScroll(true),
                        pendingGridLineChanges,
                        selectedTable,
                        pdfPageDimensions,
                        currentPage,
                        handleCellDetailClick,
                        modalState
                      );
                    } else {
                      return renderTablePreview(
                        pendingGridLineChanges.originalTableData,
                        "Before:",
                        false,
                        beforeTableRef,
                        handleTableScroll && handleTableScroll(true),
                        pendingGridLineChanges,
                        selectedTable,
                        pdfPageDimensions,
                        currentPage,
                        handleCellDetailClick,
                        modalState
                      );
                    }
                  })()}
              </Box>

              {/* Dynamic arrow based on layout */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: isColumnChange ? 0 : 1,
                  px: isColumnChange ? 1 : 0,
                  minWidth: isColumnChange ? "60px" : "auto",
                  minHeight: isColumnChange ? "auto" : "60px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    transform: isColumnChange ? "none" : "rotate(90deg)",
                    color: "primary.main",
                    fontWeight: "bold",
                  }}
                >
                  {"→"}
                </Typography>
              </Box>

              {/* After - right or bottom depending on layout */}
              <Box
                sx={{
                  flex: isColumnChange ? 1 : "none",
                  minWidth: isColumnChange ? 0 : "auto",
                }}
              >
                {renderTablePreview &&
                  (() => {
                    // For new columns, replace placeholder header with user input
                    let previewTableData = pendingGridLineChanges.newTableData;

                    if (isNewColumn && newColumnName?.trim()) {
                      // Create a copy with the user's column name instead of placeholder
                      previewTableData = {
                        ...pendingGridLineChanges.newTableData,
                        headers: pendingGridLineChanges.newTableData.headers.map(
                          (header) => {
                            // Replace any NewColumn_X placeholder with user's input
                            if (header.startsWith("NewColumn_")) {
                              return newColumnName;
                            }
                            return header;
                          }
                        ),
                      };
                    }

                    // For column splits, only show the affected columns
                    if (
                      isNewColumn &&
                      pendingGridLineChanges.splitColumnIndex !== undefined
                    ) {
                      return renderTablePreview(
                        previewTableData,
                        "After:",
                        true,
                        afterTableRef,
                        handleTableScroll && handleTableScroll(false),
                        pendingGridLineChanges,
                        selectedTable,
                        pdfPageDimensions,
                        currentPage,
                        handleCellDetailClick,
                        modalState
                      );
                    } else if (
                      isNewRow &&
                      pendingGridLineChanges.splitRowIndex !== undefined
                    ) {
                      // For row splits, only show the 2 affected rows (original + new)
                      return renderTablePreview(
                        previewTableData,
                        "After:",
                        true,
                        afterTableRef,
                        handleTableScroll && handleTableScroll(false),
                        pendingGridLineChanges,
                        selectedTable,
                        pdfPageDimensions,
                        currentPage,
                        handleCellDetailClick,
                        modalState
                      );
                    } else {
                      return renderTablePreview(
                        previewTableData,
                        "After:",
                        true,
                        afterTableRef,
                        handleTableScroll && handleTableScroll(false),
                        pendingGridLineChanges,
                        selectedTable,
                        pdfPageDimensions,
                        currentPage,
                        handleCellDetailClick,
                        modalState
                      );
                    }
                  })()}
              </Box>
            </Box>
          </Paper>
        </>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onCancel}
          disableElevation
          variant={"contained"}
          color={"secondary"}
        >
          {"Cancel"}
        </Button>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleConfirm();
          }}
          variant={"contained"}
          color={"primary"}
          disabled={isNewColumn && !newColumnName.trim()}
        >
          {isNewColumn ? "Add Column" : "Apply Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GridLineConfirmationModal;
